'use client';

import {useCallback, useContext} from 'react';
import type {XYPosition} from '@xyflow/react';
import {useReactFlow} from '@xyflow/react';
import {toast} from 'sonner';
import {captureException} from '@sentry/react';
import {trpc} from '@features/utils/trpc';
import {FlowContext} from '@features/contexts/FlowContext';
import {useSelectedScreenIds} from '@features/activeScreenIdContext/useSelectedScreenIds';
import {useSetSelectedScreenIds} from '@features/activeScreenIdContext/useSetSelectedScreenIds';
import {
  type ClipboardData,
  type ClipboardNodeSize,
  getClipboard,
  saveClipboard,
} from '../localStorage/clipboard';
import {useViewport} from '../components/useViewport';
import amplitudeService from '@features/analytics/amplitudeService';
import {
  isComponentLibraryItem,
  isComponentScreen,
} from '../utils/componentScreenId';
import {
  invalidateComponentsScreenCaches,
  invalidateScreenCaches,
} from '../utils/invalidateScreenCaches';
import {
  ESTIMATED_COMPONENT_SIZE,
  getNodeDimensions,
} from '../utils/nodeDimensions';
import {getViewportCenterInFlowCoordinates} from '@features/flow/utils/screenPositioning';

type ClipboardScreen = {
  screenId: string;
  center: XYPosition;
  size?: ClipboardNodeSize;
};

export function useScreenClipboard() {
  const {flow} = useContext(FlowContext);
  const trpcUtils = trpc.useUtils();
  const selectedScreenIds = useSelectedScreenIds();
  const setSelectedScreenIds = useSetSelectedScreenIds();
  const {screenToFlowPosition, getNode} = useReactFlow();
  const {fitViewToAll} = useViewport();
  const pasteScreens = trpc.screens.pasteScreens.useMutation({
    onSuccess: () =>
      Promise.all([
        invalidateScreenCaches(trpcUtils, flow.id),
        trpcUtils.flow.getUndoStatus.invalidate({flowId: flow.id}),
      ]),
  });
  const copyComponentScreen = trpc.screens.copyComponentScreen.useMutation({
    onSuccess: () =>
      Promise.all([
        invalidateComponentsScreenCaches(trpcUtils, flow.id),
        trpcUtils.flow.getUndoStatus.invalidate({flowId: flow.id}),
      ]),
  });

  const duplicateComponentsScreens = useCallback(
    async (clipboardScreens: ClipboardScreen[]) => {
      return (
        await copyComponentScreen.mutateAsync({
          flowId: flow.id,
          sourceScreens: clipboardScreens,
        })
      ).screenIds;
    },
    [copyComponentScreen, flow.id],
  );

  const duplicateScreens = useCallback(
    async (clipboardScreens: ClipboardScreen[]) => {
      return (
        await pasteScreens.mutateAsync({
          flowId: flow.id,
          screens: clipboardScreens,
        })
      ).screenIds;
    },
    [flow.id, pasteScreens],
  );

  const getNodeSize = useCallback(
    (screenId: string): ClipboardNodeSize => {
      const node = getNode(screenId);
      const width = node?.measured?.width ?? node?.width;
      const height = node?.measured?.height ?? node?.height;

      if (typeof width === 'number' && typeof height === 'number') {
        return {width, height};
      }

      if (isComponentLibraryItem(screenId)) {
        return {
          width: ESTIMATED_COMPONENT_SIZE,
          height: ESTIMATED_COMPONENT_SIZE,
        };
      }

      const isMobile = node?.data.screenSize === 'mobile';
      const {width: defaultWidth, height: defaultHeight} = getNodeDimensions(
        isMobile ? 'mobile' : 'web-html-css',
      );
      return {width: defaultWidth, height: defaultHeight};
    },
    [getNode],
  );

  const buildClipboardScreens = useCallback(
    (clipboard: ClipboardData): ClipboardScreen[] => {
      const center = getViewportCenterInFlowCoordinates({
        screenToFlowPosition,
      });
      return clipboard.screenIds.map((screenId) => {
        const size = clipboard.nodeSizes[screenId] ?? getNodeSize(screenId);
        return {
          screenId,
          center: {
            x: Math.round(center.x - size.width / 2),
            y: Math.round(center.y - size.height / 2),
          },
          size,
        };
      });
    },
    [getNodeSize, screenToFlowPosition],
  );

  const copy = useCallback(() => {
    const screenIdsToCopy = [...selectedScreenIds];

    if (screenIdsToCopy.length === 0) {
      toast.info('Select a screen to copy');
      return;
    }

    saveClipboard({
      version: 2,
      timestamp: Date.now(),
      sourceFlowId: flow.id,
      screenIds: screenIdsToCopy,
      nodeSizes: Object.fromEntries(
        screenIdsToCopy.map((screenId) => [screenId, getNodeSize(screenId)]),
      ),
    });

    toast.success(`${getItemTypeLiteral(screenIdsToCopy)} copied`);

    amplitudeService.logEvent('[Flow] screens copied', {
      count: screenIdsToCopy.length,
    });
  }, [selectedScreenIds, flow.id, getNodeSize]);

  const paste = useCallback(async () => {
    const clipboard = getClipboard();
    if (!clipboard || clipboard.screenIds.length === 0) {
      toast.info('Clipboard is empty');
      return;
    }

    const isSourceComponents = clipboard.screenIds.some(
      (s) => isComponentScreen(s) || isComponentLibraryItem(s),
    );
    const isTargetComponents = flow.genType === 'components';

    if (isTargetComponents !== isSourceComponents) {
      toast.error('Cannot paste screens between different project types');
      return;
    }

    if (isSourceComponents && clipboard.sourceFlowId !== flow.id) {
      toast.error(
        'Cross-project paste is not yet supported for component screens',
      );
      return;
    }

    const itemTypeLiteral = getItemTypeLiteral(clipboard.screenIds);
    const toastId = toast.loading(`Pasting ${itemTypeLiteral.toLowerCase()}`);
    try {
      const clipboardScreens = buildClipboardScreens(clipboard);
      let pastedScreenIds: string[];

      if (isSourceComponents) {
        pastedScreenIds = await duplicateComponentsScreens(clipboardScreens);
      } else {
        pastedScreenIds = await duplicateScreens(clipboardScreens);
      }

      const pastedCount = pastedScreenIds.length;
      toast.dismiss(toastId);
      toast.success(`${itemTypeLiteral} pasted`);

      if (pastedScreenIds.length > 0) {
        // Select all pasted screens; the first one becomes the primary
        // active id (mirrored to the URL).
        setSelectedScreenIds(pastedScreenIds);
      }

      if (pastedCount > 1) {
        requestAnimationFrame(() => {
          void fitViewToAll();
        });
      }

      amplitudeService.logEvent('[Flow] screens pasted', {
        count: pastedCount,
        sourceFlowId: clipboard.sourceFlowId,
        targetFlowId: flow.id,
        crossProject: clipboard.sourceFlowId !== flow.id,
      });
    } catch (error) {
      toast.dismiss(toastId);
      captureException(error);
      toast.error('Failed to paste screens');
    }
  }, [
    flow.id,
    flow.genType,
    buildClipboardScreens,
    duplicateComponentsScreens,
    duplicateScreens,
    setSelectedScreenIds,
    fitViewToAll,
  ]);

  return {copy, paste};
}

function getItemTypeLiteral(screenIds: string[]) {
  const hasScreens = screenIds.some((id) => !isComponentLibraryItem(id));
  const hasComponents = screenIds.some((id) => isComponentLibraryItem(id));
  return hasScreens && hasComponents
    ? 'Items'
    : hasComponents
      ? screenIds.length === 1
        ? 'Component'
        : 'Components'
      : screenIds.length === 1
        ? 'Screen'
        : 'Screens';
}
